/**
 * The market's real geo cascade (W8-GEO-REAL, D436, ADR-0068; owner answer D435 §5.0 items 1-2).
 *
 * `buildGeoCascade` turns the governed policy ({@link GeoCascadePolicyValue}) and the vault-resolved vendor slots into
 * the three ports the api serves — a {@link Geocoder}, a {@link Routing} and a {@link Matrix} — composed over the
 * existing S022/S025 engines, which already own the ordered cascade, the best-precision short-circuit, the shared
 * deadline and the paid-only cache. This layer adds what the donor's gateway (`packages/geo/src/config.ts`, 926aecd6)
 * did around them:
 *
 *  - vendors in the POLICY's order, each skipped (and named) when its slot is missing, disabled or keyless — the donor's
 *    `skipForMissingKey`; a port with no usable vendor is absent, so the caller can say "not configured" instead of
 *    calling a sandbox;
 *  - a durable cache for PAID vendors only (the engines enforce `paid`), TTL from the policy;
 *  - a usage + cost record for every vendor call and every cache hit (the donor's `geo_usage_log`), best-effort: a
 *    failing sink never fails a geo answer;
 *  - the drawable line completed by the self-hosted router when the winning vendor sent none (`routeComGeometria`);
 *  - the matrix ceilings (points and elements) from the policy.
 *
 * D847 (geo G1, D845 §5) adds, all driven by the policy's optional members (absent = the pre-D847 behaviour):
 *  - a ROUTER PER PURPOSE ({@link MarketGeoCascade.routingFor}): the purpose's vendor order, route mode and traffic model; the default purpose is the
 *    pre-D847 router (same vendors, same cache namespace);
 *  - a CIRCUIT BREAKER per vendor × component (`./breakers.ts`, over `@vello/geo-resilience`): a vendor that refused the key or keeps failing is
 *    skipped for its cool-down instead of being asked (and waited for) on every request;
 *  - a PER-USER GATE in front of every PAID vendor call (`beforePaid`): a cache hit, the places base and the free router never spend it; a refusal
 *    moves the cascade to the next (free) vendor;
 *  - route-cache keys SNAPPED to the governed grid; ONLY Google / HERE answers are ever stored (with the policy TTL, `null` = no expiry; a traffic-aware
 *    one per hour of the week), reused or kept as the LAST GOOD route of a cell - owner, 2026-10-29: an OSRM answer is always asked again;
 *  - the place type-ahead's own vendor order (`places.providers`), its Text Search logged as `places_search` (it was logged as `geocode`), and the
 *    session-billed Autocomplete + Details pair when the policy asks for `session`.
 *
 * Neutral: language, country filter, bias box and travel profile come from the policy (pack/market/category data).
 */
import { err, ok } from '@vello/core';
import { geoFailure, PRECISION_RANK, } from '@vello/geo';
import { createGeocodingEngine } from '@vello/geocoding';
import { createMatrixEngine, createRoutingEngine, routeCacheKey, snapRouteRequest } from '@vello/routing';
import { PAID_GEO_VENDORS, breakerRuleOf, paidCacheTtlMs, placesRuleOf, policyBounds, routePurposeRule, hourOfWeek, QUOTE_MAX_GRID_METERS, } from './cascade-policy.js';
import { throughBreaker } from './breakers.js';
import { createGooglePlaceAutocomplete } from './vendors/google-places.js';
import { GEO_SKU, googleDetailsSku, googleRouteSku, googleSessionAutocompleteLines, hereMatrixUnits, hereRouteSku, isBilledOutcome, isPoiTypes } from './pricing.js';
import { createHereGeocoder, createHereMatrix, createHerePlaceSearch, createHereRouting, hereRouteShape } from './vendors/here.js';
import { createGoogleGeocoder, createGoogleMatrix, createGooglePlaceSearch, createGoogleRouting, googleRouteShape } from './vendors/google.js';
import { createNominatimGeocoder } from './vendors/nominatim.js';
import { createOsrmMatrix, createOsrmRouting } from './vendors/osrm.js';
/** The stable reason a gated paid call carries in its failure message. */
export const GEO_USER_ALLOWANCE_SPENT = 'GEO_USER_ALLOWANCE_SPENT';
/** D870: the stable reason of a Google QUOTE call skipped because the month's Google cap is reached (`geo.spendCaps` `quoteFallsToHereAtCap`); the cascade goes on to its next vendor. */
export const GEO_SPEND_CAP_REACHED = 'GEO_SPEND_CAP_REACHED';
/** D870: the usage events of a type-ahead session that never ended in Place Details - every Autocomplete request is billed per request (research section 2.1). */
export function abandonedSessionUsage(session, at) {
    return {
        at,
        marketId: session.marketId,
        component: 'places_session_abandoned',
        providerId: session.providerId,
        elements: session.keystrokes,
        cached: false,
        outcome: 'OK',
        costMinor: 0,
        skuKey: GEO_SKU.googleAutocompleteRequests,
        billableUnits: session.keystrokes,
        reasonCode: 'GEO_COST_SESSION_ABANDONED',
    };
}
/** D870: the SKU of a geocode / reverse call per vendor (one SKU each; a reverse geocode is the same SKU). */
const GEOCODE_SKU = { here: GEO_SKU.hereGeocode, google: GEO_SKU.googleGeocoding, nominatim: GEO_SKU.nominatimGeocoding, osrm: GEO_SKU.osrmRouting };
/** Which credential field a vendor cannot run without. */
const REQUIRES = { here: 'apiKey', google: 'apiKey', nominatim: 'baseUrl', osrm: 'baseUrl' };
/** Why a vendor slot cannot serve (undefined = usable) — the cascade's own rule, also read by the setup readiness (D615). */
export function geoVendorSlotIssue(vendor, slot) {
    if (slot === undefined)
        return 'NOT_CONFIGURED';
    if (!slot.enabled)
        return 'DISABLED';
    const need = REQUIRES[vendor];
    const value = slot.credential[need];
    if (value === undefined || value.trim() === '')
        return need === 'apiKey' ? 'MISSING_KEY' : 'MISSING_BASE_URL';
    return undefined;
}
/**
 * W8G-06 (D436 review): a cache seen through the cascade that fills it. The engines key an answer by the request (scope,
 * locale, text or route); the namespace adds what decides WHICH answer the cascade gives — the usable vendors in order,
 * and for geocoding the minimum precision, the bias box and the country filter — so disabling a vendor or a governed
 * change of the cascade never serves the previous cascade's answer (it ages out by its own TTL).
 */
function namespaced(cache, prefix) {
    return { get: (key) => cache.get(prefix + key), set: (key, entry) => cache.set(prefix + key, entry) };
}
export function buildGeoCascade(deps) {
    const now = deps.now ?? Date.now;
    const policy = deps.policy;
    const skipped = [];
    const pick = (list) => list.filter((v) => {
        const why = geoVendorSlotIssue(v, deps.slots.get(v));
        if (why !== undefined && !skipped.some((s) => s.vendor === v))
            skipped.push({ vendor: v, reason: why });
        return why === undefined;
    });
    const ctx = (v) => ({ http: deps.http, credential: deps.slots.get(v).credential, timeoutMs: policy.timeoutMs });
    const costOf = (providerId, units) => {
        const slot = deps.slots.get(providerId);
        // A cost is recorded only with its currency (money is minor units + ISO 4217, never a bare number).
        const per = slot?.costCurrency !== undefined ? (slot.costPerRequestMinor ?? 0) : 0;
        return { costMinor: Math.max(0, Math.round(per * units)), ...(slot?.costCurrency !== undefined ? { currency: slot.costCurrency } : {}) };
    };
    const record = (component, providerId, elements, cached, outcome, bill) => {
        if (deps.usage === undefined)
            return;
        // A missing credential never reached the vendor: nothing billed, nothing to log.
        const cost = cached ? { costMinor: 0 } : costOf(providerId, elements);
        // D870: a cache hit bills nothing; a call that failed (the vendor did not answer) is NOT priced; a billed one carries its SKU and units.
        const billing = cached
            ? { billableUnits: 0, reasonCode: 'GEO_COST_CACHED' }
            : bill === undefined
                ? {}
                : {
                    ...(bill.skuKey !== undefined ? { skuKey: bill.skuKey } : {}),
                    ...(bill.purpose !== undefined ? { purpose: bill.purpose } : {}),
                    ...(isBilledOutcome(outcome) ? { billableUnits: bill.units, reasonCode: bill.reason ?? 'GEO_COST_PRICED' } : { billableUnits: 0, reasonCode: 'GEO_COST_FAILED_NOT_BILLED' }),
                };
        const event = { at: new Date(now()).toISOString(), marketId: deps.marketId, component, providerId, elements, cached, outcome, ...cost, ...billing };
        void deps.usage.record(event).catch(() => undefined);
    };
    const metered = (component, providerId, elements, call, bill) => async () => {
        const r = await call();
        record(component, providerId, elements, false, r.ok ? 'OK' : r.error.reason, bill);
        return r;
    };
    // D847: every vendor call goes through its breaker (skipped while open) and, for a PAID vendor, through the per-user gate (a refusal costs nothing
    // and moves the cascade on). The breaker of a vendor is per component: Google's Geocoding may answer while its Routes API refuses the same key.
    const breakerRule = breakerRuleOf(policy);
    const breakerOf = (v, component) => deps.breakers?.of(v, component, breakerRule);
    // D847 review: a refusal of the per-user gate shapes this request's answers (a free fallback stood in) - such an answer is never kept as the cell's
    // last good route. `kind` undefined = a call the gate never counts (a free Autocomplete keystroke of a session).
    let gateRefusals = 0;
    const timing = { timeoutMs: policy.timeoutMs, now };
    const guarded = (v, breakerComponent, kind, call) => throughBreaker(breakerOf(v, breakerComponent), v, async () => {
        if (kind !== undefined && PAID_GEO_VENDORS.includes(v) && deps.beforePaid !== undefined && !(await deps.beforePaid(kind))) {
            gateRefusals += 1;
            return err(geoFailure('RATE_LIMITED', `${GEO_USER_ALLOWANCE_SPENT}: ${kind}`, { retriable: false }));
        }
        return call();
    }, timing);
    const options = {
        locale: policy.language,
        scope: { market: deps.marketId, ...(policy.region !== undefined ? { country: policy.region.alpha2 } : {}) },
    };
    // ---- geocoding ------------------------------------------------------------------------------------
    const geocodingVendors = pick(policy.geocoding.providers);
    const geocoderOf = (v) => v === 'here' ? createHereGeocoder(ctx(v), policy.region?.alpha3 !== undefined ? { countryAlpha3: policy.region.alpha3 } : {}) : v === 'google' ? createGoogleGeocoder(ctx(v)) : createNominatimGeocoder(ctx(v));
    const geocodingBackends = geocodingVendors.map((v) => {
        const inner = geocoderOf(v);
        return {
            id: v,
            paid: PAID_GEO_VENDORS.includes(v),
            geocoder: {
                geocode: (q, o) => guarded(v, 'geocode', 'geocode', metered('geocode', v, 1, () => inner.geocode(q, o), { skuKey: GEOCODE_SKU[v], units: 1 })),
                reverseGeocode: (q, o) => guarded(v, 'geocode', 'geocode', metered('reverse', v, 1, () => inner.reverseGeocode(q, o), { skuKey: GEOCODE_SKU[v], units: 1 })),
            },
        };
    });
    // D847: `null` = no expiry (the far sentinel), 0 = never cached.
    const cacheTtlMs = paidCacheTtlMs(policy, now()) ?? 0;
    let geocoder;
    if (geocodingBackends.length > 0) {
        const engine = createGeocodingEngine(geocodingBackends, {
            minPrecision: policy.geocoding.minPrecision,
            ...(deps.geocodeCache !== undefined && cacheTtlMs > 0 ? { cache: namespaced(deps.geocodeCache, `geo:${geocodingVendors.join('>')}|${policy.geocoding.minPrecision}|${JSON.stringify(policy.boundsMicrodegrees ?? null)}|${policy.region?.alpha2 ?? ''}|`), cacheTtlMs } : {}),
            now,
        });
        const hit = (component, r) => {
            if (r.ok && r.value.provenance.cached)
                record(component, r.value.provenance.providerId, 1, true, 'OK');
            return r;
        };
        const bounds = policyBounds(policy);
        // D854 (L2-004): the engine keeps its best-so-far answer below `minPrecision` (flagged degraded); a forward search is an ADDRESS, so a match under
        // the governed floor (a city or region centroid for a nonsense street) is dropped on every tier, and nothing left is NOT_FOUND.
        const minRank = PRECISION_RANK[policy.geocoding.minPrecision];
        const atFloor = (r) => {
            if (!r.ok)
                return r;
            const matches = r.value.matches.filter((m) => PRECISION_RANK[m.precision] >= minRank);
            if (matches.length === r.value.matches.length)
                return r;
            return matches.length === 0 ? err(geoFailure('NOT_FOUND', `no match reached the governed minimum precision ${policy.geocoding.minPrecision}`)) : ok({ ...r.value, matches });
        };
        geocoder = {
            geocode: async (q, o) => atFloor(hit('geocode', await engine.geocode({ ...q, ...(q.bias === undefined && bounds !== undefined ? { bias: bounds } : {}) }, { ...options, ...o }))),
            reverseGeocode: async (q, o) => hit('reverse', await engine.reverseGeocode(q, { ...options, ...o })),
        };
    }
    // ---- D822: the POI search, over the type-ahead's vendors (D847: `places.providers`, else the geocoding vendors that have one) -----
    const placesRule = placesRuleOf(policy);
    const placeSearchVendors = pick(placesRule.providers);
    const placeSearchOf = (v) => v === 'here' ? createHerePlaceSearch(ctx(v), policy.region?.alpha3 !== undefined ? { countryAlpha3: policy.region.alpha3 } : {}) : createGooglePlaceSearch(ctx(v));
    const placeSearchers = placeSearchVendors.map((v) => ({ id: v, inner: placeSearchOf(v) }));
    let placeSearch;
    if (placeSearchers.length > 0) {
        placeSearch = {
            async searchPlaces(q, o) {
                let last = err(geoFailure('NOT_FOUND', 'no place search answered'));
                for (const s of placeSearchers) {
                    last = await guarded(s.id, 'places', 'places', metered('places_search', s.id, 1, () => s.inner.searchPlaces(q, { ...options, ...o }), { skuKey: s.id === 'google' ? GEO_SKU.googleTextSearchPro : GEO_SKU.hereDiscover, units: 1 }));
                    if (last.ok && last.value.matches.length > 0)
                        return last;
                }
                return last;
            },
        };
    }
    // ---- D847: the session-billed type-ahead (Autocomplete + Details), when the policy asks for it ----------------------------------
    let placeAutocomplete;
    const autocompleteVendor = placesRule.autocomplete === 'session' ? placeSearchVendors.find((v) => v === 'google') : undefined;
    if (autocompleteVendor !== undefined) {
        const inner = createGooglePlaceAutocomplete(ctx(autocompleteVendor));
        placeAutocomplete = {
            // A keystroke of a session costs nothing once the session ends in Details: logged with 0 billable elements and NOT spent from the per-user
            // allowance (D847 review); the session is tracked so an abandoned one (no Details) is still counted. Details is the billed call: gated.
            // D870: the request itself bills nothing yet (`GEO_COST_SESSION_PENDING`): the session settles at its end - Place Details (the count and the field mask decide the SKUs) or abandonment.
            autocomplete: async (q, o) => {
                const r = await guarded(autocompleteVendor, 'places', undefined, metered('places_autocomplete', autocompleteVendor, 0, () => inner.autocomplete(q, { ...options, ...o }), { units: 0, reason: 'GEO_COST_SESSION_PENDING' }));
                if (r.ok)
                    await (async () => deps.placesSessions?.touch(q.sessionToken, deps.marketId, autocompleteVendor, r.value.map((p) => ({ placeId: p.placeId, poi: isPoiTypes(p.types) }))))().catch(() => undefined);
                return r;
            },
            // D870 (owner 2026-10-31): the business name (a Pro field, billed Enterprise + Atmosphere) is asked ONLY when the chosen suggestion was an establishment, from the session's predictions kept
            // server-side - a client cannot force the expensive mask. The call and its session settle into SKUs: Essentials (first 12 requests per request) or Enterprise + Atmosphere (requests free).
            details: async (q, o) => {
                const token = q.sessionToken !== undefined && q.sessionToken.trim() !== '' ? q.sessionToken : undefined;
                const poi = token !== undefined && deps.placesSessions !== undefined ? await deps.placesSessions.isPoi(token, q.placeId).catch(() => false) : false;
                const r = await guarded(autocompleteVendor, 'places', 'places', async () => {
                    const res = await inner.details({ ...q, includeName: poi }, { ...options, ...o });
                    record('place_details', autocompleteVendor, 1, false, res.ok ? 'OK' : res.error.reason, { skuKey: googleDetailsSku(poi), units: 1, reason: 'GEO_COST_SESSION_SETTLED' });
                    return res;
                });
                if (r.ok && token !== undefined && deps.placesSessions !== undefined) {
                    const closed = await deps.placesSessions.close(token).catch(() => undefined);
                    if (closed !== undefined) {
                        for (const line of googleSessionAutocompleteLines(closed.requests, poi ? 'enterprise' : 'essentials')) {
                            record('places_autocomplete', autocompleteVendor, 0, false, 'OK', { skuKey: line.skuKey, units: line.units, reason: 'GEO_COST_SESSION_SETTLED' });
                        }
                    }
                }
                return r;
            },
        };
    }
    // ---- routing (+ the drawable line from the self-hosted router), one router per purpose (D847) -----------------------------
    const routingOf = (v, rule) => v === 'here' ? createHereRouting(ctx(v), { mode: rule.mode, twoWheeler: rule.twoWheeler, traffic: rule.traffic })
        : v === 'google' ? createGoogleRouting(ctx(v), { mode: rule.mode, traffic: rule.traffic, twoWheeler: rule.twoWheeler })
            : createOsrmRouting(ctx(v), { mode: rule.mode });
    const geometryRouter = policy.routing.completeGeometry && geoVendorSlotIssue('osrm', deps.slots.get('osrm')) === undefined ? createOsrmRouting(ctx('osrm')) : undefined;
    // D847: a governed grid snaps the cache key of every point (the same trip from a few metres away is the same answer); absent -> the exact key.
    const grid = policy.cache?.gridMeters;
    /**
     * The cache key of a purpose: the points snapped to the governed grid (a QUOTE never to more than {@link QUOTE_MAX_GRID_METERS}), and - D847 review -
     * a TRAFFIC-AWARE answer under its hour of the week in the market's zone (kept indefinitely, but a 3 a.m. quote never reuses a rush-hour answer).
     */
    const keyOfRule = (rule) => {
        const g = grid === undefined ? undefined : rule.purpose === 'quote' ? Math.min(grid, QUOTE_MAX_GRID_METERS) : grid;
        const base = g !== undefined ? (request, o) => routeCacheKey(snapRouteRequest(request, g), o) : routeCacheKey;
        return rule.traffic === 'TRAFFIC_AWARE' ? (request, o) => `how=${hourOfWeek(now(), deps.timeZone)}|${base(request, o)}` : base;
    };
    const lastGoodTtlMs = (policy.cache?.lastGoodTtlSeconds ?? 0) * 1000;
    const lastGoodCache = deps.lastGoodCache !== undefined && lastGoodTtlMs > 0 ? deps.lastGoodCache : undefined;
    const buildPurpose = (rule) => {
        const vendors = pick(rule.providers);
        if (vendors.length === 0)
            return undefined;
        const backends = vendors.map((v) => {
            const inner = routingOf(v, rule);
            // D870: the SKU is what the adapter really sends (the same shape functions the adapters use), the purpose rides on the row.
            const billOf = (req) => ({
                skuKey: v === 'google' ? googleRouteSku(googleRouteShape(req, { mode: rule.mode, traffic: rule.traffic, twoWheeler: rule.twoWheeler }))
                    : v === 'here' ? hereRouteSku(hereRouteShape(req, { mode: rule.mode, twoWheeler: rule.twoWheeler, traffic: rule.traffic }))
                        : GEO_SKU.osrmRouting,
                units: 1,
                purpose: rule.purpose,
            });
            const capped = v === 'google' && rule.purpose === 'quote' && deps.quoteCapGate !== undefined;
            return {
                id: v,
                paid: PAID_GEO_VENDORS.includes(v),
                routing: {
                    route: async (req, o) => {
                        // D870: the month's Google cap is reached and the quote falls to the next vendor; an unreadable spend FAILS OPEN (Google is asked as usual).
                        if (capped && !(await deps.quoteCapGate().catch(() => true)))
                            return err(geoFailure('RATE_LIMITED', GEO_SPEND_CAP_REACHED, { retriable: false }));
                        return guarded(v, 'route', 'route', metered('route', v, 1, () => inner.route(req, o), billOf(req)));
                    },
                },
            };
        });
        // The default purpose keeps the pre-D847 namespace byte for byte (its cached answers stay hits); a mode, a traffic model or a car-routed moto is a
        // different answer and gets its own.
        // (D847 review: the two-wheeler default is OFF, so `tw=1` marks the variant and the default purpose keeps the pre-D847 namespace.)
        // D870: HERE now honours `traffic` (`departureTime=any` when unaware), so an unaware answer is a different answer from one cached before - `tf=u` keeps the old (time-aware) HERE answers apart.
        const variant = [rule.mode !== 'fastest' ? `m=${rule.mode}` : '', rule.traffic !== 'TRAFFIC_UNAWARE' ? `t=${rule.traffic}` : 'tf=u', rule.twoWheeler ? 'tw=1' : ''].filter((x) => x !== '').join(',');
        const keyOf = keyOfRule(rule);
        const prefix = `route:${vendors.join('>')}|${variant !== '' ? `${variant}|` : ''}`;
        const engine = createRoutingEngine(backends, {
            now,
            keyOf,
            ...(deps.routeCache !== undefined && cacheTtlMs > 0 ? { cache: namespaced(deps.routeCache, prefix), cacheTtlMs } : {}),
            // Owner, 2026-10-29: the cascade is EXACTLY the configured order - a vendor that refuses or fails hands over to the NEXT one (never straight
            // to the caller's last resort), and each gets its turn within the deadline (see `trip-route.ts` for the budget).
            everyBackendATurn: true,
            continueOnInvalidRequest: true,
        });
        const lastGoodKey = (request, o) => `lastgood:${rule.mode}|${keyOf(request, { ...options, ...o })}`;
        const routing = {
            route: async (request, o) => {
                const merged = { ...options, ...o };
                const started = now();
                const r = await engine.route(request, merged);
                if (!r.ok)
                    return r;
                if (r.value.provenance.cached)
                    record('route', r.value.provenance.providerId, 1, true, 'OK');
                // Owner, 2026-10-29: only a Google / HERE answer, unshaped by a per-user refusal, becomes the cell's last good route (an OSRM answer is
                // always asked again - it is free).
                else if (lastGoodCache !== undefined && PAID_GEO_VENDORS.includes(r.value.provenance.providerId) && gateRefusals === 0) {
                    // D847: the last good route of this cell, for the next total outage (best effort).
                    const entry = { result: r.value, expiresAt: now() + lastGoodTtlMs };
                    await (async () => lastGoodCache.set(lastGoodKey(request, merged), entry))().catch(() => undefined);
                }
                const primary = r.value.routes[0];
                if (primary === undefined || primary.geometry !== undefined || geometryRouter === undefined)
                    return r;
                // W8G-04 (D436 review): the drawable line only within what is left of the caller's deadline — never a further
                // full timeout after a late answer.
                const remaining = merged.deadlineMs === undefined ? undefined : merged.deadlineMs - (now() - started);
                if (remaining !== undefined && remaining <= 0)
                    return r;
                const g = await metered('geometry', 'osrm', 1, () => geometryRouter.route(request, remaining === undefined ? merged : { ...merged, deadlineMs: remaining }), { skuKey: GEO_SKU.osrmRouting, units: 1, purpose: rule.purpose })();
                const geometry = g.ok ? g.value.routes[0]?.geometry : undefined;
                if (geometry === undefined)
                    return r;
                return ok({ ...r.value, routes: [{ ...primary, geometry }, ...r.value.routes.slice(1)] });
            },
        };
        return {
            routing,
            rule,
            active: vendors,
            ...(lastGoodCache !== undefined
                ? {
                    lastGood: async (request, o) => {
                        const hit = await (async () => lastGoodCache.get(lastGoodKey(request, o)))().catch(() => undefined);
                        return hit !== undefined && hit.expiresAt > now() ? { ...hit.result, degraded: true, provenance: { ...hit.result.provenance, cached: true } } : undefined;
                    },
                }
                : {}),
        };
    };
    const purposes = new Map();
    const routingFor = (purpose = 'default') => {
        if (!purposes.has(purpose))
            purposes.set(purpose, buildPurpose(routePurposeRule(policy, purpose)));
        return purposes.get(purpose);
    };
    const defaultRouting = routingFor('default');
    const routingVendors = defaultRouting?.active ?? pick(routePurposeRule(policy, 'default').providers);
    const routing = defaultRouting?.routing;
    // ---- matrix ---------------------------------------------------------------------------------------
    const matrixVendors = pick(policy.matrix.providers);
    const matrixOf = (v) => (v === 'osrm' ? createOsrmMatrix(ctx(v)) : v === 'here' ? createHereMatrix(ctx(v)) : createGoogleMatrix(ctx(v)));
    const matrixBackends = matrixVendors.map((v) => {
        const inner = matrixOf(v);
        return {
            id: v,
            paid: PAID_GEO_VENDORS.includes(v),
            // D870: HERE counts `5 x max(origins, destinations)` transactions when both are 5 or more (Vello logged origins x destinations); Google's legacy Distance Matrix and OSRM count elements.
            matrix: {
                matrix: (req, o) => {
                    const elements = req.origins.length * req.destinations.length;
                    const bill = v === 'here' ? { skuKey: GEO_SKU.hereMatrix, units: hereMatrixUnits(req.origins.length, req.destinations.length) } : { skuKey: v === 'google' ? GEO_SKU.googleDistanceMatrix : GEO_SKU.osrmMatrix, units: elements };
                    return guarded(v, 'matrix', 'route', metered('matrix', v, elements, () => inner.matrix(req, o), bill));
                },
            },
        };
    });
    let matrix;
    if (matrixBackends.length > 0) {
        const engine = createMatrixEngine(matrixBackends, { maxElements: policy.matrix.maxElements, maxPoints: policy.matrix.maxPoints, now });
        matrix = { matrix: (req, o) => engine.matrix(req, { ...options, ...o }) };
    }
    return {
        marketId: deps.marketId,
        ...(geocoder !== undefined ? { geocoder } : {}),
        ...(placeSearch !== undefined ? { placeSearch } : {}),
        ...(routing !== undefined ? { routing } : {}),
        routingFor,
        ...(placeAutocomplete !== undefined ? { placeAutocomplete } : {}),
        ...(matrix !== undefined ? { matrix } : {}),
        options,
        active: { geocoding: geocodingVendors, routing: routingVendors, matrix: matrixVendors, placeSearch: placeSearchVendors },
        skipped,
    };
}
//# sourceMappingURL=cascade.js.map